/**
 * Collateral Monitoring Dashboard
 *
 * Displays collateral health across all client positions with CHA tier alerts,
 * concentration risk, and individual position health indicators. Uses DashboardGrid
 * for metrics and DataGrid for the positions table.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { advisorClient } from '../api/client';
import {
  ADVISOR_SERVICE_MISSING_TITLE,
  AdvisorServiceMissingError,
  AdvisorServiceNotice,
  advisorFailure,
} from '../components/AdvisorServiceNotice';
import type { components } from '@aaimplatform/institutional-types/advisor-tools';
import type { ColumnDef, GaugeMetric } from '@aaimplatform/ui';
import {
  Heading,
  BodyText,
  Card,
  MetricCard,
  DashboardGrid,
  DashboardWidget,
  DataGrid,
  GaugeCluster,
} from '@aaimplatform/ui';


type CollateralPosition = components['schemas']['CollateralPosition'];
type CollateralSummary = components['schemas']['CollateralSummary'];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Threshold bands for collateral health assessment.
 * Green (>= 150%): healthy coverage
 * Amber (120-150%): monitoring required
 * Red (< 120%): critical, a CHA tier change needing immediate attention
 */
const COVERAGE_THRESHOLDS = {
  healthy: 1.5,
  warning: 1.2,
} as const;

const HEALTH_INDICATOR_STYLES: Record<string, { label: string; className: string }> = {
  healthy: { label: 'Healthy', className: 'bg-growth/10 text-growth' },
  warning: { label: 'Monitor', className: 'bg-warning/10 text-warning' },
  critical: { label: 'Critical', className: 'bg-error/10 text-error' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercent(decimal: number): string {
  return `${(decimal * 100).toFixed(1)}%`;
}

function getHealthStatus(coverageRatio: number): 'healthy' | 'warning' | 'critical' {
  if (coverageRatio >= COVERAGE_THRESHOLDS.healthy) return 'healthy';
  if (coverageRatio >= COVERAGE_THRESHOLDS.warning) return 'warning';
  return 'critical';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CollateralMonitoringPage() {
  const navigate = useNavigate();

  const { data: summary, isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: ['collateral-summary'],
    queryFn: async (): Promise<CollateralSummary | null> => {
      const { data, error, response } = await advisorClient.GET('/api/collateral/summary');
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load collateral summary');
      return (data ?? null) as CollateralSummary | null;
    },
  });

  const { data: positions, isLoading: positionsLoading, error: positionsError } = useQuery({
    queryKey: ['collateral-positions'],
    queryFn: async (): Promise<CollateralPosition[]> => {
      const { data, error, response } = await advisorClient.GET('/api/collateral/positions');
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load collateral positions');
      return (data ?? []) as CollateralPosition[];
    },
  });

  const serviceMissing =
    summaryError instanceof AdvisorServiceMissingError || positionsError instanceof AdvisorServiceMissingError;

  // Build gauge cluster data for coverage ratio
  const gaugeMetrics = useMemo<GaugeMetric[]>(() => {
    if (!summary) return [];
    return [
      {
        id: 'overall-coverage',
        label: 'Overall Coverage',
        value: summary.overallCoverageRatio * 100,
        min: 0,
        max: 200,
        thresholds: [
          { value: 120, color: 'rgb(var(--color-error))' },
          { value: 150, color: 'rgb(var(--color-warning))' },
          { value: 200, color: 'rgb(var(--color-success))' },
        ],
        unit: '%',
      },
      {
        id: 'alerts',
        label: 'Active Alerts',
        value: summary.concentrationAlerts + summary.marginCallCount, // term-register-skip: contract field name in advisor-tools.openapi.yaml; the rename is the GH #2656 class
        min: 0,
        max: 20,
        thresholds: [
          { value: 3, color: 'rgb(var(--color-success))' },
          { value: 8, color: 'rgb(var(--color-warning))' },
          { value: 20, color: 'rgb(var(--color-error))' },
        ],
        unit: '',
      },
    ];
  }, [summary]);

  // Alert counts
  const alertCounts = useMemo(() => {
    if (!positions) return { healthy: 0, warning: 0, critical: 0 };
    return positions.reduce(
      (acc, p) => {
        const status = getHealthStatus(p.coverageRatio);
        acc[status]++;
        return acc;
      },
      { healthy: 0, warning: 0, critical: 0 },
    );
  }, [positions]);

  // DataGrid columns
  const columns = useMemo<ColumnDef<CollateralPosition, unknown>[]>(() => [
    {
      id: 'clientName',
      header: 'Client',
      accessorKey: 'clientName',
      size: 180,
      enableSorting: true,
      cell: ({ row }: { row: { original: CollateralPosition } }) => (
        <button
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            navigate(`/clients/${row.original.clientId}`);
          }}
          className="font-semibold text-accent hover:underline text-left"
        >
          {row.original.clientName}
        </button>
      ),
    },
    {
      id: 'loanNumber',
      header: 'Loan #',
      accessorKey: 'loanNumber',
      size: 110,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="font-mono text-primary">{String(getValue())}</span>
      ),
    },
    {
      id: 'assetType',
      header: 'Asset Type',
      accessorKey: 'assetType',
      size: 140,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="text-primary capitalize">{String(getValue()).replace(/_/g, ' ')}</span>
      ),
    },
    {
      id: 'collateralValue',
      header: 'Collateral Value',
      accessorKey: 'collateralValue',
      size: 150,
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="font-mono tabular-nums">{formatCurrency(Number(getValue()))}</span>
      ),
    },
    {
      id: 'loanOutstanding',
      header: 'Loan Outstanding',
      accessorKey: 'loanOutstanding',
      size: 150,
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="font-mono tabular-nums">{formatCurrency(Number(getValue()))}</span>
      ),
    },
    {
      id: 'coverageRatio',
      header: 'Coverage Ratio',
      accessorKey: 'coverageRatio',
      size: 130,
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ getValue }: { getValue: () => unknown }) => {
        const ratio = Number(getValue());
        const status = getHealthStatus(ratio);
        const textColor =
          status === 'healthy' ? 'text-growth' :
          status === 'warning' ? 'text-warning' :
          'text-error';
        return (
          <span className={`font-mono tabular-nums font-semibold ${textColor}`}>
            {formatPercent(ratio)}
          </span>
        );
      },
    },
    {
      id: 'health',
      header: 'Health',
      accessorFn: (row: CollateralPosition) => getHealthStatus(row.coverageRatio),
      size: 120,
      enableSorting: true,
      cell: ({ row }: { row: { original: CollateralPosition } }) => {
        const status = getHealthStatus(row.original.coverageRatio);
        const indicator = HEALTH_INDICATOR_STYLES[status];
        return (
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${indicator.className}`}>
            {indicator.label}
          </span>
        );
      },
    },
    {
      id: 'marginCallThreshold', // term-register-skip: contract field name in advisor-tools.openapi.yaml; the rename is the GH #2656 class
      header: 'Call Threshold',
      accessorKey: 'marginCallThreshold', // term-register-skip: contract field name in advisor-tools.openapi.yaml; the rename is the GH #2656 class
      size: 130,
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="font-mono tabular-nums text-tertiary">{formatPercent(Number(getValue()))}</span>
      ),
    },
  ], [navigate]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            Real-time collateral health, CHA tier alerts, and concentration risk
          </BodyText>
        </div>
      </div>

      {serviceMissing && <AdvisorServiceNotice className="mb-6" />}

      {/* Top-Level KPIs */}
      <DashboardGrid columns={4} gap="compact" className="mb-6" loading={summaryLoading}>
        <DashboardWidget id="total-collateral">
          <MetricCard
            label="Total Collateral Value"
            value={summary ? formatCurrency(summary.totalCollateralValue) : '--'}
            valueColor="primary"
          />
        </DashboardWidget>
        <DashboardWidget id="total-exposure">
          <MetricCard
            label="Total Loan Exposure"
            value={summary ? formatCurrency(summary.totalLoanExposure) : '--'}
            valueColor="primary"
          />
        </DashboardWidget>
        <DashboardWidget id="margin-alerts">
          <MetricCard
            label="Critical CHA Alerts"
            value={String(alertCounts.critical)}
            valueColor={alertCounts.critical > 0 ? 'accent' : 'growth'}
            change={alertCounts.warning > 0 ? `${alertCounts.warning} monitoring` : undefined}
            changeType="neutral"
          />
        </DashboardWidget>
        <DashboardWidget id="coverage-ratio">
          <MetricCard
            label="Overall Coverage"
            value={summary ? formatPercent(summary.overallCoverageRatio) : '--'}
            valueColor={
              summary && summary.overallCoverageRatio >= COVERAGE_THRESHOLDS.healthy
                ? 'growth'
                : 'accent'
            }
          />
        </DashboardWidget>
      </DashboardGrid>

      {/* Gauge Cluster and Concentration Warnings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {gaugeMetrics.length > 0 && (
          <Card>
            <Heading level={3} as={3} size="card" className="mb-4">Coverage Gauges</Heading>
            <GaugeCluster title="Coverage Metrics" gauges={gaugeMetrics} />
          </Card>
        )}

        <Card>
          <Heading level={3} as={3} size="card" className="mb-4">Alert Summary</Heading>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-error/10 rounded border border-error/20">
              <div>
                <BodyText className="text-error font-semibold">Critical (below {formatPercent(COVERAGE_THRESHOLDS.warning)})</BodyText>
                <BodyText className="text-error text-sm">Immediate attention required</BodyText>
              </div>
              <span className="text-2xl font-mono font-semibold text-error">
                {alertCounts.critical}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-warning/10 rounded border border-warning/20">
              <div>
                <BodyText className="text-warning font-semibold">Monitoring ({formatPercent(COVERAGE_THRESHOLDS.warning)} - {formatPercent(COVERAGE_THRESHOLDS.healthy)})</BodyText>
                <BodyText className="text-warning text-sm">Enhanced surveillance</BodyText>
              </div>
              <span className="text-2xl font-mono font-semibold text-warning">
                {alertCounts.warning}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-growth/10 rounded border border-growth/20">
              <div>
                <BodyText className="text-growth font-semibold">Healthy (above {formatPercent(COVERAGE_THRESHOLDS.healthy)})</BodyText>
                <BodyText className="text-growth text-sm">Within guidelines</BodyText>
              </div>
              <span className="text-2xl font-mono font-semibold text-growth">
                {alertCounts.healthy}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Collateral Positions Grid */}
      <Heading level={3} as={3} size="card" className="mb-4">Collateral Positions</Heading>
      {positionsError && !serviceMissing ? (
        <Card>
          <div className="p-4 bg-error/10 border border-error/20 rounded">
            <BodyText className="text-error font-semibold">Error Loading Positions</BodyText>
            <BodyText className="text-error mt-1">
              Failed to retrieve collateral position data.
            </BodyText>
          </div>
        </Card>
      ) : (
        <DataGrid
          data={positions ?? []}
          columns={columns}
          isLoading={positionsLoading}
          enableVirtualization
          virtualizedHeight={500}
          enableGlobalFilter
          enableColumnFilters
          enableColumnVisibility
          enablePagination
          defaultPageSize={25}
          pageSizeOptions={[10, 25, 50, 100]}
          enableExport
          striped
          hoverable
          density="default"
          getRowId={(row) => `${row.clientId}-${row.loanNumber}-${row.assetType}`}
          emptyMessage={serviceMissing ? ADVISOR_SERVICE_MISSING_TITLE : 'No collateral positions found.'}
        />
      )}
    </div>
  );
}
